"use client";

import { Link } from "@/components/LocaleLink";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "@/lib/hooks/useTranslation";

export default function LegalPage() {
  const { locale } = useTranslation();

  const content = locale === "fr" ? {
    title: "Mentions Légales",
    lastUpdate: "Dernière mise à jour : 15/06/2026",
    sections: [
      {
        title: "1. Éditeur du site",
        content: "Le site velyq.com et le service Velyq sont édités par :",
        list: [
          "Nom : Mathieu Neumar",
          "Statut : Entrepreneur individuel (micro-entreprise / auto-entrepreneur)",
          "Adresse : 51 rue du Général Leclerc, 94270 Le Kremlin-Bicêtre, France",
          "N° SIRET : 89349160500026",
          "TVA : non applicable, art. 293 B du CGI (franchise en base de TVA)",
          "Directeur de la publication : Mathieu Neumar",
          "Email de contact : contact@velyq.com"
        ]
      },
      {
        title: "2. Hébergement",
        content: "Le site et les données sont hébergés au sein de l'Union européenne (région europe-west1, Belgique) par :",
        list: [
          "Google Cloud Platform — Google Cloud EMEA Limited, Velasco, Clanwilliam Place, Dublin 2, Irlande (infrastructure : Cloud Run, Cloud SQL et Cloud Storage en région europe-west1).",
          "Envoi des emails : Brevo (Sendinblue SAS), 7 rue de Madrid, 75008 Paris, France."
        ]
      },
      {
        title: "3. Propriété intellectuelle",
        content: "L'ensemble du site velyq.com — sa structure, ses textes, son code, sa charte graphique, son logo, sa mascotte et tous les éléments qui le composent — est la propriété exclusive de Mathieu Neumar (marque « Velyq »), sauf mention contraire. Toute reproduction, représentation ou exploitation, totale ou partielle, par quelque procédé que ce soit, sans autorisation écrite préalable, est interdite. Les contenus que vous importez (CV, lettres, etc.) restent votre propriété."
      },
      {
        title: "4. Objet du service",
        content: "Velyq est une plateforme d'aide à la recherche d'emploi assistée par intelligence artificielle : analyse de CV (ATS), simulation d'entretien (texte et vocal), génération de lettres de motivation, optimisation de profil LinkedIn et suivi de candidatures. Le service repose sur des modèles d'IA tiers ; les résultats sont fournis à titre indicatif et ne constituent ni une garantie d'embauche, ni un conseil juridique ou professionnel."
      },
      {
        title: "5. Conditions d'utilisation et de vente",
        content: "L'accès et l'utilisation du service sont régis par les Conditions Générales d'Utilisation et de Vente (CGU/CGV), disponibles sur la page dédiée. Elles précisent notamment les offres payantes, le droit de rétractation et le recours à un médiateur de la consommation."
      },
      {
        title: "6. Protection des données personnelles",
        content: "Le traitement de vos données personnelles (dont les CV importés) est décrit en détail dans notre Politique de confidentialité. Conformément au RGPD et à la loi « Informatique et Libertés » du 6 janvier 1978 modifiée, vous disposez de droits d'accès, de rectification, d'effacement, de limitation, de portabilité et d'opposition, ainsi que du droit d'introduire une réclamation auprès de la CNIL. Contact : contact@velyq.com."
      },
      {
        title: "7. Cookies et traceurs",
        content: "Le site utilise des cookies et traceurs (mesure d'audience, prévention de la fraude). Les traceurs non essentiels ne sont déposés qu'après votre consentement via le bandeau dédié. Le détail est précisé dans la Politique de confidentialité."
      },
      {
        title: "8. Droit applicable et litiges",
        content: "Les présentes mentions sont régies par le droit français. En cas de litige avec un consommateur, celui-ci peut recourir gratuitement au médiateur de la consommation indiqué dans les CGV, ou à la plateforme européenne de règlement en ligne des litiges (https://ec.europa.eu/consumers/odr). À défaut de résolution amiable, les tribunaux français sont compétents."
      },
      {
        title: "9. Contact",
        content: "Pour toute question : contact@velyq.com"
      }
    ]
  } : {
    title: "Legal Notice",
    lastUpdate: "Last updated: June 15, 2026",
    sections: [
      {
        title: "1. Site Publisher",
        content: "The website velyq.com and the Velyq service are published by:",
        list: [
          "Name: Mathieu Neumar",
          "Status: Sole trader (French micro-entreprise / auto-entrepreneur)",
          "Address: 51 rue du Général Leclerc, 94270 Le Kremlin-Bicêtre, France",
          "SIRET Number: 89349160500026",
          "VAT: not applicable, Art. 293 B of the French Tax Code (VAT exemption)",
          "Publication director: Mathieu Neumar",
          "Contact email: contact@velyq.com"
        ]
      },
      {
        title: "2. Hosting",
        content: "The site and its data are hosted within the European Union (region europe-west1, Belgium) by:",
        list: [
          "Google Cloud Platform — Google Cloud EMEA Limited, Velasco, Clanwilliam Place, Dublin 2, Ireland (infrastructure: Cloud Run, Cloud SQL and Cloud Storage in region europe-west1).",
          "Email delivery: Brevo (Sendinblue SAS), 7 rue de Madrid, 75008 Paris, France."
        ]
      },
      {
        title: "3. Intellectual Property",
        content: "The entire velyq.com website — its structure, texts, code, design, logo, mascot and all components — is the exclusive property of Mathieu Neumar (\"Velyq\" brand), unless stated otherwise. Any reproduction, representation or exploitation, in whole or in part, by any means, without prior written authorization, is prohibited. Content you upload (CVs, letters, etc.) remains your property."
      },
      {
        title: "4. Purpose of the Service",
        content: "Velyq is an AI-assisted job-search platform: resume (ATS) analysis, interview simulation (text and voice), cover-letter generation, LinkedIn profile optimization and application tracking. The service relies on third-party AI models; results are provided for guidance only and constitute neither a guarantee of employment nor legal or professional advice."
      },
      {
        title: "5. Terms of Use and Sale",
        content: "Access to and use of the service are governed by the Terms of Use and Sale (available on the dedicated page), which set out the paid plans, the right of withdrawal and the consumer-mediation procedure."
      },
      {
        title: "6. Personal Data Protection",
        content: "The processing of your personal data (including uploaded CVs) is described in detail in our Privacy Policy. Under the GDPR, you have the rights of access, rectification, erasure, restriction, portability and objection, as well as the right to lodge a complaint with the French data-protection authority (CNIL). Contact: contact@velyq.com."
      },
      {
        title: "7. Cookies and Trackers",
        content: "The site uses cookies and trackers (audience measurement, fraud prevention). Non-essential trackers are only set after your consent via the dedicated banner. Details are provided in the Privacy Policy."
      },
      {
        title: "8. Governing Law and Disputes",
        content: "These notices are governed by French law. A consumer may use, free of charge, the consumer mediator listed in the Terms of Sale, or the European online dispute-resolution platform (https://ec.europa.eu/consumers/odr). Failing an amicable resolution, the French courts have jurisdiction."
      },
      {
        title: "9. Contact",
        content: "For any questions: contact@velyq.com"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <Link href="/">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-semibold">{content.title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Logo size="sm" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto max-w-4xl px-4 py-12">
        <p className="mb-8 text-sm text-muted-foreground">{content.lastUpdate}</p>

        <div className="space-y-8">
          {content.sections.map((section, index) => (
            <section key={index}>
              <h2 className="mb-4 text-xl font-semibold">{section.title}</h2>
              <p className="text-muted-foreground">{section.content}</p>
              {section.list && (
                <ul className="mt-4 list-disc space-y-2 pl-6 text-muted-foreground">
                  {section.list.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* Links to other legal pages */}
        <div className="mt-12 flex flex-wrap gap-4 border-t pt-8">
          <Link href="/privacy" className="text-sm text-primary hover:underline">
            {locale === "fr" ? "Politique de confidentialité" : "Privacy Policy"}
          </Link>
          <Link href="/terms" className="text-sm text-primary hover:underline">
            {locale === "fr" ? "CGU/CGV" : "Terms & Conditions"}
          </Link>
        </div>
      </main>
    </div>
  );
}
